import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import Phone from 'services/Phone'
import CustomerService from 'services/Customer'
import CheckBox from 'components/CheckBox'
import SelectAsync from 'components/Inputs/SelectAsync'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import mergeCustomersValidator from 'validators/mergeCustomersValidator'
import { FaChevronRight } from 'react-icons/fa'
import { Formik } from 'formik'
import { DateTime } from 'luxon'

import {
  Form,
  FormField,
  Label,
  Row,
  CheckRow,
  CheckGrid,
  InfoText,
  ButtonText,
  WarningText,
  ErrorContainer
} from './styles'

export default function MergeCustomersModal ({
  visible,
  onClose: handleClose
}) {
  const [errorMessage, setErrorMessage] = useState (null)
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const getCustomers = async query => {
    try {
      const { data: customers } = await cutileiApi.get (
        `/businesses/${businessId}/customers?query=${encodeURIComponent (query)}`, requestConfig
      )

      const sortedCustomers = CustomerService.sortCustomersByQuery (customers.data, query)

      return sortedCustomers.map (customer => ({
        label: `${customer.name}${customer.phone && ` - ${Phone.formatPhone (customer.phone)}`}`,
        value: customer
      }))
    } catch (error) {
      logApiError (error)
    }
  }

  const formatDate = date => DateTime.fromISO (date).toFormat ('dd/MM/yyyy')
  const toggleConfirmDialog = () => setShowConfirmDialog (!showConfirmDialog)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='lg' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Unificar clientes</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={mergeCustomersValidator}
          initialValues={{
            'prev_customer': null,
            'new_customer': null,
            'new_email': '',
            'new_phone': '',
            'new_birth': ''
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const { prev_customer, new_customer, new_email, new_phone, new_birth } = values
              const prevCustomer = prev_customer.value
              const newCustomer = new_customer.value

              const newCustomerData = {
                ...newCustomer,
                email: new_email,
                phone: new_phone,
                birth: DateTime.fromISO (new_birth).toISODate (),
                balance: prevCustomer.balance + newCustomer.balance
              }
            
              await cutileiApi.post (
                `/businesses/${businessId}/customers/${prevCustomer.id}/merge`,
                newCustomerData,
                requestConfig
              )
            
              setSubmitting (false)
              handleClose ()
            } catch (error) {
              setSubmitting (false)
              setErrorMessage (getApiErrorMessage (error))
              logApiError (error)
            }
          }}
        >
          {function MergeCustomersForm ({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleSubmit
          }) {
            useEffect (() => {
              if (values.prev_customer) {
                setFieldValue ('new_email', values.new_customer.value.email || '')
                setFieldValue ('new_phone', values.new_customer.value.phone || '')
                setFieldValue ('new_birth', values.new_customer.value.birth || '')
              }
            }, [values.new_customer])

            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(toggleConfirmDialog, isSubmitting)}>
                    <InfoText>
                      Se você cadastrou um cliente mais de uma vez com e-mail ou telefone diferente, você pode transferir todos os dados do cliente antigo para o novo. O cadastro antigo será removido.
                    </InfoText>
                    <Row>
                      <FormField>
                        <Label htmlFor='prev_customer'>
                          Transferir dados de:
                        </Label>
                        <SelectAsync
                          id='prev_customer'
                          name='prev_customer'
                          placeholder='Cliente...'
                          value={values.prev_customer}
                          loadOptions={getCustomers}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          error={errors.prev_customer}
                          touched={touched.prev_customer}
                          onMenuOpen={() => setFieldValue ('prev_customer', null)}
                        />
                        {touched.prev_customer && errors.prev_customer && (
                          <WarningText>
                            {errors.prev_customer}
                          </WarningText>
                        )}
                      </FormField>
                      <FaChevronRight size={20} style={{alignSelf: 'flex-start', marginTop: 35}}/>
                      <FormField>
                        <Label htmlFor='new_customer'>
                          Para:
                        </Label>
                        <SelectAsync
                          id='new_customer'
                          name='new_customer'
                          placeholder='Cliente...'
                          value={values.new_customer}
                          loadOptions={getCustomers}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          error={errors.new_customer}
                          touched={touched.new_customer}
                          onMenuOpen={() => setFieldValue ('new_customer', null)}
                        />
                        {touched.new_customer && errors.new_customer && (
                          <WarningText>
                            {errors.new_customer}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    {values.prev_customer && values.new_customer && (
                      <FormField>
                        <InfoText>
                          Selecione quais dados deseja manter no cadastro unificado:
                        </InfoText>
                        <CheckGrid>
                          {(
                            values.prev_customer.value.email &&
                            values.prev_customer.value.email !== values.new_customer.value.email
                          ) ? (
                            <CheckRow>
                              <CheckBox
                                id={values.prev_customer.value.email}
                                value={values.new_email === values.prev_customer.value.email}
                                onValueChange={value => {
                                  value && setFieldValue ('new_email', values.prev_customer.value.email)
                                }}
                              />
                              <Label htmlFor={values.prev_customer.value.email}>
                                {values.prev_customer?.value.email}
                              </Label>
                            </CheckRow>
                          ) : (
                            <span/>
                          )}
                          {values.new_customer.value.email ? (
                            <CheckRow>
                              <CheckBox
                                id={values.new_customer.value.email}
                                value={values.new_email === values.new_customer.value.email}
                                onValueChange={value => {
                                  value && setFieldValue ('new_email', values.new_customer.value.email)
                                }}
                              />
                              <Label htmlFor={values.new_customer.value.email}>
                                {values.new_customer.value.email}
                              </Label>
                            </CheckRow>
                          ) : (
                            <span/>
                          )}
                          {(
                            values.prev_customer.value.phone &&
                            values.prev_customer.value.phone !== values.new_customer.value.phone
                          ) ? (
                            <CheckRow>
                              <CheckBox
                                id={values.prev_customer.value.phone}
                                value={values.new_phone === values.prev_customer.value.phone}
                                onValueChange={value => {
                                  value && setFieldValue ('new_phone', values.prev_customer.value.phone)
                                }}
                              />
                              <Label htmlFor={values.prev_customer.value.phone}>
                                {Phone.formatPhone (values.prev_customer.value.phone)}
                              </Label>
                            </CheckRow>
                          ) : (
                            <span/>
                          )}
                          {values.new_customer.value.phone ? (
                            <CheckRow>
                              <CheckBox
                                id={values.new_customer.value.phone}
                                value={values.new_phone === values.new_customer.value.phone}
                                onValueChange={value => {
                                  value && setFieldValue ('new_phone', values.new_customer.value.phone)
                                }}
                              />
                              <Label htmlFor={values.new_customer.value.phone}>
                                {Phone.formatPhone (values.new_customer.value.phone)}
                              </Label>
                            </CheckRow>
                          ) : (
                            <span/>
                          )}
                          {(
                            values.prev_customer.value.birth &&
                            values.prev_customer.value.birth !== values.new_customer.value.birth
                          ) ? (
                            <CheckRow>
                              <CheckBox
                                id={values.prev_customer.value.birth}
                                value={values.new_birth === values.prev_customer.value.birth}
                                onValueChange={value => {
                                  value && setFieldValue ('new_birth', values.prev_customer.value.birth)
                                }}
                              />
                              <Label htmlFor={values.prev_customer.value.birth}>
                                {formatDate (values.prev_customer.value.birth)}
                              </Label>
                            </CheckRow>
                          ) : (
                            <span/>
                          )}
                          {values.new_customer.value.birth ? (
                            <CheckRow>
                              <CheckBox
                                id={values.new_customer.value.birth}
                                value={values.new_birth === values.new_customer.value.birth}
                                onValueChange={value => {
                                  value && setFieldValue ('new_birth', values.new_customer.value.birth)
                                }}
                              />
                              <Label htmlFor={values.new_customer.value.birth}>
                                {formatDate (values.new_customer.value.birth)}
                              </Label>
                            </CheckRow>
                          ) : (
                            <span/>
                          )}
                        </CheckGrid>
                        <InfoText style={{width: 500}}>
                          Todos os agendamentos, comandas, pacotes, créditos, dívidas, anexos e registros do cadastro antigo do usuário serão transferidos para o novo cadastro.
                        </InfoText>
                      </FormField>
                    )}
                    {errorMessage && (
                      <ErrorContainer>
                        <ButtonText>{errorMessage}</ButtonText>
                      </ErrorContainer>
                    )}
                    <AlertDialog
                      visible={showConfirmDialog}
                      title='Atenção!'
                      message={`Deseja realmente transferir todos os dados de ${values.prev_customer?.value.name} para ${values.new_customer?.value.name}?`}
                      confirmText='Sim'
                      cancelText='Não'
                      onConfirm={handleSubmit}
                      onClose={toggleConfirmDialog}
                    />
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label='Unificar'
                    onClick={toggleConfirmDialog}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={180}
                  />
                </DialogFooter>
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
